// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import axios, { AxiosError } from 'axios';
import type {
  ProviderAccessPageContext,
  ProviderFormSubmissionRequest,
  ProviderSubmissionResult,
  ProviderRejectionRequest,
  ProviderFeedbackRequest,
  ProviderAccessErrorResponse,
} from '../types/providerAccess';

/**
 * Public API client for provider Open Access endpoints.
 * No authentication required -- security is via the token in the URL path.
 */
const publicApi = axios.create({
  baseURL: '/api/provider-access',
  headers: { 'Content-Type': 'application/json' },
});

/** Validate token and get page context */
export const validateToken = async (token: string): Promise<ProviderAccessPageContext> => {
  const response = await publicApi.get<ProviderAccessPageContext>(`/${token}`);
  return response.data;
};

/** Submit structured form data */
export const submitForm = async (
  token: string,
  formData: ProviderFormSubmissionRequest,
): Promise<ProviderSubmissionResult> => {
  const response = await publicApi.post<ProviderSubmissionResult>(`/${token}/submit`, formData);
  return response.data;
};

/** Upload a document file with optional wizard step/field context */
export const uploadDocument = async (
  token: string,
  file: File,
  stepId?: string,
  fieldKey?: string,
  onProgress?: (percent: number) => void,
): Promise<ProviderSubmissionResult> => {
  const formData = new FormData();
  formData.append('file', file);
  if (stepId) {
    formData.append('stepId', stepId);
  }
  if (fieldKey) {
    formData.append('fieldKey', fieldKey);
  }

  const response = await publicApi.post<ProviderSubmissionResult>(`/${token}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event) => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded * 100) / event.total));
      }
    },
  });
  return response.data;
};

/** Reject the LOA */
export const rejectLoa = async (
  token: string,
  request: ProviderRejectionRequest,
): Promise<ProviderSubmissionResult> => {
  const response = await publicApi.post<ProviderSubmissionResult>(`/${token}/reject`, request);
  return response.data;
};

/** Submit optional feedback after completing the wizard */
export const submitFeedback = async (
  token: string,
  request: ProviderFeedbackRequest,
): Promise<ProviderSubmissionResult> => {
  const response = await publicApi.post<ProviderSubmissionResult>(`/${token}/feedback`, request);
  return response.data;
};

/**
 * Phase 9: accept a Change of Agency.
 *
 * <p>Hits the Phase 5 {@code POST /api/provider-access/{token}/coa-accept}
 * endpoint. The body is a free-form JSON map (currently empty — the form
 * is a stub) so future fields like effective-date confirmation can be
 * added without changing this signature.
 */
export const acceptCoa = async (
  token: string,
  formData: Record<string, unknown> = {},
): Promise<ProviderSubmissionResult> => {
  const response = await publicApi.post<ProviderSubmissionResult>(
    `/${token}/coa-accept`,
    formData,
  );
  return response.data;
};

/**
 * Phase 9: reject a Change of Agency.
 *
 * <p>Hits the Phase 5 {@code POST /api/provider-access/{token}/coa-reject}
 * endpoint. Reuses the same {@link ProviderRejectionRequest} shape as
 * the LOA reject path so the existing rejection-reason picker can drive
 * both flows without a parallel DTO.
 */
export const rejectCoa = async (
  token: string,
  request: ProviderRejectionRequest,
): Promise<ProviderSubmissionResult> => {
  const response = await publicApi.post<ProviderSubmissionResult>(
    `/${token}/coa-reject`,
    request,
  );
  return response.data;
};

/**
 * New Business: issue a policy against the application — the provider confirms
 * the application by returning the issued policy number. Hits
 * {@code POST /api/provider-access/{token}/new-business-issue}; the backend
 * mints the Policy and completes the request.
 */
export const issueNewBusiness = async (
  token: string,
  policyNumber: string,
): Promise<ProviderSubmissionResult> => {
  const response = await publicApi.post<ProviderSubmissionResult>(
    `/${token}/new-business-issue`,
    { policyNumber },
  );
  return response.data;
};

/**
 * New Business: reject the application. Reuses {@link ProviderRejectionRequest}
 * (same reason picker as LOA/CoA). Hits
 * {@code POST /api/provider-access/{token}/new-business-reject}.
 */
export const rejectNewBusiness = async (
  token: string,
  request: ProviderRejectionRequest,
): Promise<ProviderSubmissionResult> => {
  const response = await publicApi.post<ProviderSubmissionResult>(
    `/${token}/new-business-reject`,
    request,
  );
  return response.data;
};

/** Extract a user-friendly error from an axios error */
export const extractError = (err: unknown): ProviderAccessErrorResponse => {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError<ProviderAccessErrorResponse>;
    if (axiosErr.response?.data?.code) {
      return axiosErr.response.data;
    }
    return {
      error: axiosErr.message || 'An unexpected error occurred',
      code: 'TOKEN_NOT_FOUND',
    };
  }
  return {
    error: 'An unexpected error occurred',
    code: 'TOKEN_NOT_FOUND',
  };
};

const providerAccessApi = {
  validateToken,
  submitForm,
  uploadDocument,
  rejectLoa,
  submitFeedback,
  acceptCoa,
  rejectCoa,
  issueNewBusiness,
  rejectNewBusiness,
  extractError,
};

export default providerAccessApi;
